import { ViewsState, initialViewsState, View, ViewType } from './Views'
import { ViewAddedEvent, ViewEvent, ViewEvents, ViewUpdates } from './ViewsEvents'

const isExternalViewType = (viewType: ViewType): boolean => {
	return viewType === ViewType.POKER_TABLE_EXTERNAL || viewType === ViewType.SLOTS_EXTERNAL
}

function exhaustiveCheck(_value: never): void {
	// Compile-time exhaustiveness guard. If a new ViewType is added
	// without a case above, this line produces a TS error.
	// At runtime, falls through gracefully (reducers must not throw).
}

function createView(payload: ViewAddedEvent['payload']): View {
	const base = { id: payload.id, name: payload.name, isActive: false, tabIconType: payload.tabIconType, isHidden: payload.isHidden }
	switch (payload.type) {
		case ViewType.LOGIN:
		case ViewType.LOBBY:
		case ViewType.SLOTS_LOBBY:
			return { ...base, type: payload.type, isGame: false as const }
		case ViewType.POKER_TABLE:
			return { ...base, type: payload.type, isGame: true as const, tableId: payload.tableId as number, groupId: payload.groupId }
		case ViewType.POKER_TABLE_EXTERNAL:
			return { ...base, type: payload.type, isGame: true as const, tableId: payload.tableId as number, groupId: payload.groupId }
		case ViewType.MULTI_TABLE:
			return { ...base, type: payload.type, isGame: true as const }
		case ViewType.TOURNAMENT:
			return { ...base, type: payload.type, isGame: false as const, tournamentId: payload.tournamentId }
		case ViewType.SLOTS:
		case ViewType.BLACKJACK:
		case ViewType.GAP_SLOTS:
			return { ...base, type: payload.type, isGame: true as const, tableId: payload.tableId as string, groupId: payload.groupId }
		case ViewType.SLOTS_EXTERNAL:
			return { ...base, type: payload.type, isGame: true as const, groupId: payload.groupId }
		case ViewType.PROFILE:
			return { ...base, type: payload.type, isGame: false as const }
		case ViewType.EXTERNAL_PAGE:
			return { ...base, type: payload.type, isGame: false as const, url: payload.url }
		default:
			exhaustiveCheck(payload.type)
			return { ...base, type: payload.type, isGame: false } as View
	}
}

function updateView<T extends View>(view: T, updates: ViewUpdates): T {
	return { ...view, ...updates } as T
}

export const viewsReducer = (state: ViewsState = initialViewsState, event: ViewEvent): ViewsState => {
	switch (event.type) {
		case ViewEvents.VIEW_ADDED_EVENT: {
			const payload = event.payload
			const existingView = state.views.find((view) => view.id === payload.id)
			if (existingView) {
				return state
			}
			return {
				...state,
				views: [...state.views, createView(payload)],
				multiTableViewId: payload.type === ViewType.MULTI_TABLE
					? payload.id
					: state.multiTableViewId,
			}
		}
		case ViewEvents.VIEW_ACTIVATED_EVENT: {
			const viewExists = state.views.find((view) => view.id === event.payload.id)
			if (viewExists === undefined) {
				return state
			}
			
			const isExternalView = isExternalViewType(viewExists.type)
			
			if (isExternalView) {
				if (state.externalWindowActiveViewIds.includes(event.payload.id) && viewExists.isActive) {
					return state
				}
			} else {
				if (state.mainWindowActiveViewId === event.payload.id && viewExists.isActive) {
				return state
			}
			}
			
			const updatedViews = state.views.map((view) => {
				if (view.id === event.payload.id) {
					return updateView(view, { isActive: true })
				}

				if (!isExternalView && !isExternalViewType(view.type) && view.isActive) {
					return updateView(view, { isActive: false })
				}

				return view
			})
			
			if (isExternalView) {
				const newExternalIds = state.externalWindowActiveViewIds.includes(event.payload.id)
					? state.externalWindowActiveViewIds
					: [...state.externalWindowActiveViewIds, event.payload.id]
				
				return {
					...state,
					views: updatedViews,
					externalWindowActiveViewIds: newExternalIds,
				}
			} else {
				return {
				...state,
					views: updatedViews,
					mainWindowActiveViewId: event.payload.id,
				}
			}
		}
		case ViewEvents.VIEW_REMOVED_EVENT: {
			const existingView = state.views.find((view) => view.id === event.payload.id)
			if (existingView === undefined) {
				return state
			}
			
			return {
				...state,
				views: state.views.filter((view) => view.id !== event.payload.id),
				mainWindowActiveViewId: state.mainWindowActiveViewId === event.payload.id 
					? null 
					: state.mainWindowActiveViewId,
				externalWindowActiveViewIds: state.externalWindowActiveViewIds.filter(
					id => id !== event.payload.id
				),
				multiTableViewId: state.multiTableViewId === event.payload.id 
					? null 
					: state.multiTableViewId,
			}
		}
		case ViewEvents.VIEW_DEACTIVATED_EVENT: {
			const existingView = state.views.find((view) => view.id === event.payload.id)
			if (existingView === undefined) {
				return state
			}
			
			const isExternalView = isExternalViewType(existingView.type)
			
			if (!existingView.isActive) {
				if (isExternalView && !state.externalWindowActiveViewIds.includes(event.payload.id)) {
					return state
				}
				if (!isExternalView && state.mainWindowActiveViewId !== event.payload.id) {
				return state
			}
			}
			
			const updatedViews = state.views.map((view) =>
				view.id === event.payload.id ? updateView(view, { isActive: false }) : view
			)
			
			if (isExternalView) {
			return {
				...state,
					views: updatedViews,
					externalWindowActiveViewIds: state.externalWindowActiveViewIds.filter(
						id => id !== event.payload.id
					),
				}
			} else {
				return {
					...state,
					views: updatedViews,
					mainWindowActiveViewId: state.mainWindowActiveViewId === event.payload.id 
						? null 
						: state.mainWindowActiveViewId,
				}
			}
		}
		case ViewEvents.VIEW_UPDATED_EVENT: {
			const existingView = state.views.find((view) => view.id === event.payload.id)
			if (existingView === undefined) {
				return state
			}
			return {
				...state,
				views: state.views.map((view) =>
					view.id === event.payload.id ? updateView(view, event.payload.updates) : view
				),
			}
		}
		default:
			return state
	}
}
